import type { RowSelectionState } from '@tanstack/react-table';
import { convertRelativeDateRangeToFixedDateRange } from 'components/datepicker/utils';
import { TimesheetScopes } from 'components/timesheets/types';
import { UNRESTRICTED_FROM, UNRESTRICTED_TO } from 'constants/datetime';
import {
  COLUMN_BILLABLE_HOURS,
  COLUMN_BILLED,
  COLUMN_DAY,
  COLUMN_ESTIMATED_HOURS,
  COLUMN_ESTIMATED_MONEY,
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_NON_BILLABLE_HOURS,
  COLUMN_PROJECT,
  COLUMN_STATE,
  ITEMS_WITH_NO_TABLE_COLUMN,
} from 'constants/filters';
import {
  HAS_ADDED_WIDGET_TO_REPORT,
  HAS_FILTERED_REPORTS,
  HAS_SEEN_CAN_REORDER,
} from 'constants/hints/reports';
import { PREFERRED_REPORT_FILTER, PREFERRED_REPORT_VIEW } from 'constants/user';
import * as widgetConst from 'constants/widgets';
import type { PublicReportListItem, RelationsType } from 'factories/superreports';
import {
  newReportWidgetFactory,
  publicReportListItemFactory,
  reportListItemFactory,
  widgetRowHourFactory,
  widgetWithBillableVsNonBillableDataFactory,
  widgetWithBilledVsUnbilledDataFactory,
  widgetWithDataFactory,
  widgetWithDonutDataFactory,
  widgetWithGraphDataFactory,
} from 'factories/superreports';
import { intersection, omit } from 'lodash-es';
import { DateTime } from 'luxon';
import { isDayLockedForUser } from 'redux/modules/dayProperties';
import { getDayProperties } from 'redux/modules/dayProperties.selectors';
import {
  getHasLoadedHourStates,
  getHourStates,
  getNonDeletedHourStates,
} from 'redux/modules/hourStates';
import { defaultState, defaultStateId } from 'redux/modules/hourStates.constants';
import { readHourSummaryData } from 'redux/modules/hourSummary.selectors';
import type { HoursDetailsBatchSelection, HoursDictionary } from 'redux/modules/hours.types';
import { HOUR_DETAILS_INITIAL_FILTER } from 'redux/modules/hours.types';
import { extractSingleCurrency } from 'redux/modules/invoice.utils';
import type {
  ClientDataEntities,
  DayDataEntities,
  GenericWidgetRow,
  LinkedWorkDataEntity,
  ProjectDataEntities,
  ReportExportSettingsPayload,
  ReportFilterTypes,
  ReportQueryExtended,
  TagDataEntity,
  TeamDataEntity,
  UserDataEntity,
  WidgetWithData,
} from 'redux/modules/superreports.types';
import { MINIMUM_REPORTS_ON_DASHBOARD, WidgetDataType } from 'redux/modules/superreports.types';
import type { TimesheetsDatatableRow } from 'redux/modules/superreports-datatable.types';
import { getTagEntities, getTags, getTagsWithChildren } from 'redux/modules/tags.selectors';
import type { TagsWithChildrenDictionary } from 'redux/modules/tags.types';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import {
  getAccountCurrency,
  getAccountFeatures,
  getAccountReportSettings,
  getCurrentAccount,
  getCurrentAccountColumnOrders,
  getHasHourStatesFeature,
  getHasTicIntegrationsSupport,
  getStartOfWeek,
} from 'redux/selectors/accounts';
import { getClients } from 'redux/selectors/clients';
import { getHourEntities, getHourResult, getHours, getHourXhrUIState } from 'redux/selectors/hours';
import { getAllLoadedProjects, getProjectVariantMap } from 'redux/selectors/projects';
import { getTeamsAsDropdownOptions, getUserLedUserIds } from 'redux/selectors/teams';
import {
  getIsUserAdmin,
  getUser,
  getUserPermissions,
  getUserRoleId,
  getUserTimeFormat,
} from 'redux/selectors/user';
import { getAllUsers } from 'redux/selectors/users';
import { createSelector } from 'reselect';
import type { StartOfWeek } from 'types/account';
import type { HoursDetailsFilter } from 'types/hours';
import type { Project } from 'types/project';
import { TagSettings } from 'types/project';
import type { ReportEntity } from 'types/reports';
import { EntityDataKey } from 'types/reports';
import type { TagBase } from 'types/tag';
import { TagFilterMode } from 'types/tag';
import type { PickByValue } from 'types/utility';
import { formatCurrency } from 'utils/currency';
import debug from 'utils/debug';
import { filterHoursByState } from 'utils/filter-list';
import { enrichProjectsOnHours, isHourLocked } from 'utils/hours';
import type { SortableValue } from 'utils/naturalSort';
import { naturalCompare } from 'utils/naturalSort';
import { reduceBySummingAttributes } from 'utils/numbers';
import { getProjectColor } from 'utils/project';
import { sortReportableValues } from 'utils/report';
import { getMaxCostAndDurationSummary } from 'utils/summary';
import {
  determineFilterStatus,
  filterProjectsByStatusOrIds,
  formatGenericWidgetValues,
  getCommonTagsForFiltering,
  getSignatureFromDraftAndQuery,
  hourToGenericWidgetValues,
  transformLegacyFiltersToStateIds,
} from 'utils/superreports';
import { notNull } from 'utils/typesafe';
import type { IState } from '../state';
import { getCurrenciesById } from './currencies';
import { getAssetRevisionStatus, getIsAppInitialized } from './global';
import {
  getPinnedProjectIdsSet,
  getPinnedReportEntities,
  getPinnedReportIds,
  getPinnedReportIdsSet,
} from './pins';
import { mapHourColumnToTable, mapTableColumnToSort } from './utils/superreports';

const readReportEntities = (state: IState) => state.superreports.reportEntities;
const readReportResult = (state: IState) => state.superreports.reportResult;
const readReportFilter = (state: IState) => state.superreports.reportFilter;
const readPublicReportEntities = (state: IState) => state.superreports.publicReportEntities;
const readPublicReportResult = (state: IState) => state.superreports.publicReportResult;
const readPublicReportFilter = (state: IState) => state.superreports.publicReportFilter;
const readReportListFilter = (state: IState) => state.superreports.reportListFilter;
const readReportSelection = (state: IState) => state.superreports.selection;
const readReportQuery = (state: IState) => state.superreports.query;
const readClientReportQuery = (state: IState) => state.superreports.clientReportQuery;
const readReportDraft = (state: IState) => state.superreports.draft;
const readReportDraftSignature = (state: IState) => state.superreports.draftSignature;
const readReportDataDayEntities = (state: IState) => state.superreports.dayDataEntities;
const readReportDataDayResult = (state: IState) => state.superreports.dayDataResult;
const readReportDataProjectEntities = (state: IState) => state.superreports.projectDataEntities;
const readReportDataProjectResult = (state: IState) => state.superreports.projectDataResult;
const readReportDataClientEntities = (state: IState) => state.superreports.clientDataEntities;
const readReportDataClientResult = (state: IState) => state.superreports.clientDataResult;
const readReportDataUserEntities = (state: IState) => state.superreports.userDataEntities;
const readReportDataUserResult = (state: IState) => state.superreports.userDataResult;
const readReportDataLabelEntities = (state: IState) => state.superreports.labelDataEntities;
const readReportDataLabelResult = (state: IState) => state.superreports.labelDataResult;
const readReportDataTeamEntities = (state: IState) => state.superreports.teamDataEntities;
const readReportDataTeamResult = (state: IState) => state.superreports.teamDataResult;
const readReportDataLinkedWorkEntities = (state: IState) =>
  state.superreports.linkedWorkDataEntities;
const readReportDataLinkedWorkResult = (state: IState) => state.superreports.linkedWorkDataResult;
const readEmailedReport = (state: IState) => state.superreports.emailedReport;
const readReportViewStatus = (state: IState) => state.superreports.reportView;
const readReportMaybeHasMore = (state: IState) => state.superreports.reportsMaybeHasMore;
const readPublicReportMaybeHasMore = (state: IState) => state.superreports.publicReportMaybeHasMore;
const readTimesheetColumns = (state: IState) => state.superreports.timesheetColumns;
const readTimesheetTaskColumns = (state: IState) => state.superreports.timesheetTaskColumns;
const readWidgetMetadata = (state: IState) => state.superreports.widgetMetadata;
const readReportTotals = (state: IState) => state.superreports.totals;
const readXhrStatus = (state: IState) => state.superreports.xhrStatus;
const readTimesheetMaybeHasMore = (state: IState) => state.superreports.timesheetMaybeHasMore;
const readReportDraftWidgets = (state: IState) => state.superreports.draft.widgets;
const readPublicReportTimestamp = (state: IState) => state.superreports.publicReportTimestamp;
const readMinimumWidgetWidth = (state: IState) => state.superreports.minimumWidgetWidth;
const readTimesheetTotalCount = (state: IState) => state.superreports.timesheetTotalCount;
const readReportExportSettings = (state: IState) => state.superreports.reportExportSettings;
const readNextExportDate = (state: IState) => state.superreports.nextExportDate;

export const getXhrStatus = readXhrStatus;
export const getXhrStatusUiState = createSelector([readXhrStatus], xhrStatus =>
  xhrStatusUIState(xhrStatus)
);

export const getWidgetMetadataXhrStatus = createSelector([getXhrStatus], xhrStatus => {
  return xhrStatusUIState(xhrStatus, 'widgetMetadata');
});

export const getWidgetMetadata = readWidgetMetadata;
export const getMinimumWidgetWidth = readMinimumWidgetWidth;

export const getReportQuery = createSelector(
  [readReportQuery, getStartOfWeek],
  (query: ReportQueryExtended, startOfWeek: StartOfWeek): ReportQueryExtended => {
    if ('relativeDateRange' in query && query.relativeDateRange !== undefined) {
      const dateRange = convertRelativeDateRangeToFixedDateRange(
        query.relativeDateRange,
        startOfWeek
      );
      return {
        ...query,
        ...dateRange,
      };
    }
    return query;
  }
);
export const getReportTotals = readReportTotals;
export const getReportSelection = readReportSelection;

export const getReportEntities = readReportEntities;
export const getHasMultipleCurrencies = (state: IState) => !!state.superreports.draft.multiCurrency;
export const getTagFilterMode = createSelector(
  [getReportQuery],
  query => query.filters.label_filter || TagFilterMode.OR
);

export const getIsReportQueryReady = createSelector([getReportQuery], query =>
  Boolean(query.from && query.to && query.filters.user_ids.length > 0)
);

// Project ids that pass the query.filters
export const getReportDataProjectFilteredResult = createSelector(
  [getReportQuery, readReportDataProjectResult],
  ({ filters }, result): Array<number> => {
    if (Array.isArray(filters.project_ids)) {
      const hasFilter = !!filters?.project_ids.length;
      return hasFilter ? filters.project_ids : result.map(projectId => +projectId);
    }
    return result;
  }
);

// User ids that pass the query.filters
export const getReportDataUserFilteredResult = createSelector(
  [getReportQuery, readReportDataUserResult],
  ({ filters }, result): Array<number> => {
    if (Array.isArray(filters.user_ids)) {
      const hasFilter = !!filters?.user_ids.length;
      return hasFilter ? filters.user_ids : result.map(userId => +userId);
    }
    return result;
  }
);

// Label ids that pass the query.filters
export const getReportDataLabelFilteredResult = createSelector(
  [getReportQuery, readReportDataLabelResult],
  ({ filters }, result): Array<number> => {
    if (Array.isArray(filters.label_ids)) {
      const hasFilter = !!filters?.label_ids.length;
      return hasFilter ? filters.label_ids : result;
    }
    return result;
  }
);

export interface EntityLookups {
  hourEntities: HoursDictionary;
  hourResult: Array<number>;

  userEntities: {
    [id: number]: UserDataEntity;
  };
  userResult: Array<number>;
  labelResult: Array<number>;
  labelEntities: {
    [id: number]: TagDataEntity;
  };
  projectResult: Array<number>;
  projectEntities: ProjectDataEntities;
  clientResult: Array<number>;
  clientEntities: ClientDataEntities;
  dayResult: Array<string>;
  dayEntities: DayDataEntities;
  teamResult: Array<number>;
  teamEntities: { [id: number]: TeamDataEntity };
  linkedWorkResult: Array<number>;
  linkedWorkEntities: { [id: number]: LinkedWorkDataEntity };
}

const getEntityLookupEntities = createSelector(
  [
    readReportDataUserEntities,
    readReportDataLabelEntities,
    readReportDataProjectEntities,
    readReportDataClientEntities,
    readReportDataDayEntities,
    readReportDataTeamEntities,
    readReportDataLinkedWorkEntities,
    getHourEntities,
    getTagEntities,
  ],
  (
    userEntities,
    reportLabelEntities,
    projectEntities,
    clientEntities,
    dayEntities,
    teamEntities,
    linkedWorkEntities,
    hourEntities,
    globalTagEntities
  ) => {
    // Merge global tags with report-specific labels to ensure all tags are available
    // for hours in expanded rows. Report labels take precedence over global tags.
    // This fixes TT-4326 where tags disappear when editing time entries.
    const labelEntities = { ...globalTagEntities, ...reportLabelEntities };

    return {
      userEntities,
      labelEntities,
      projectEntities,
      clientEntities,
      dayEntities,
      teamEntities,
      linkedWorkEntities,
      hourEntities,
    };
  }
);

const getEntityLookupResults = createSelector(
  [
    getReportDataUserFilteredResult, // readReportDataUserResult,
    getReportDataLabelFilteredResult, // readReportDataLabelResult,
    getReportDataProjectFilteredResult, // readReportDataProjectResult,
    readReportDataClientResult,
    readReportDataDayResult,
    readReportDataTeamResult,
    readReportDataLinkedWorkResult,
    getHourResult,
  ],
  (
    userResult,
    labelResult,
    projectResult,
    clientResult,
    dayResult,
    teamResult,
    linkedWorkResult,
    hourResult
  ) => ({
    userResult,
    labelResult,
    projectResult,
    clientResult,
    dayResult,
    teamResult,
    linkedWorkResult,
    hourResult,
  })
);

const getEntityLookups = createSelector(
  [getEntityLookupEntities, getEntityLookupResults],
  (entities, results): EntityLookups => {
    return {
      ...entities,
      ...results,
    };
  }
);

export const getWidgets = createSelector(
  [
    readReportDraftWidgets,
    getEntityLookups,
    getReportQuery,
    readReportSelection,
    getUserTimeFormat,
    readWidgetMetadata,
    readReportTotals,
    getCurrenciesById,
    getStartOfWeek,
  ],
  (
    widgets,
    {
      clientEntities,
      clientResult,
      dayEntities,
      dayResult,
      hourEntities,
      hourResult,
      labelResult,
      labelEntities,
      projectEntities,
      projectResult,
      userEntities,
      userResult,
      teamEntities,
      teamResult,
      linkedWorkEntities,
      linkedWorkResult,
    },
    query,
    selection,
    timeFormat,
    entityMetadata,
    totals,
    currencyLookup,
    startOfWeek
  ): Array<WidgetWithData> => {
    return widgets
      .filter(widget => !widget._deleted)
      .map(widget => {
        switch (widget.widget_type) {
          case widgetConst.WIDGET_USERS_LIST:
          case widgetConst.WIDGET_USERS_HBAR: {
            const maxValues = getMaxCostAndDurationSummary(userResult, userEntities);

            return widgetWithDataFactory(
              widget,
              userEntities,
              userResult,
              {
                maxValues,
                hourEntities,
                hourResult,
                projectEntities,
                labelEntities,
                userEntities,
                teamEntities,
                selection,
              },
              timeFormat,
              query.filters,
              entityMetadata,
              currencyLookup,
              totals
            );
          }
          case widgetConst.WIDGET_LABELS_LIST:
          case widgetConst.WIDGET_LABELS_HBAR: {
            const maxValues = getMaxCostAndDurationSummary(labelResult, labelEntities);

            return widgetWithDataFactory(
              widget,
              labelEntities,
              labelResult,
              {
                maxValues,
                hourEntities,
                hourResult,
                projectEntities,
                labelEntities,
                userEntities,
                teamEntities,
                selection,
              },
              timeFormat,
              query.filters,
              entityMetadata,
              currencyLookup,
              totals
            );
          }

          case widgetConst.WIDGET_TEAMS_LIST: {
            const maxValues = getMaxCostAndDurationSummary(teamResult, teamEntities);
            return widgetWithDataFactory(
              widget,
              teamEntities,
              teamResult,
              {
                maxValues,
                hourEntities,
                hourResult,
                labelEntities,
                projectEntities,
                userEntities,
                teamEntities,
                selection,
              },
              timeFormat,
              query.filters,
              entityMetadata,
              currencyLookup,
              totals
            );
          }

          case widgetConst.WIDGET_PROJECTS_LIST:
          case widgetConst.WIDGET_PROJECTS_HBAR: {
            const maxValues = getMaxCostAndDurationSummary(projectResult, projectEntities);
            return widgetWithDataFactory(
              widget,
              projectEntities,
              projectResult,
              {
                maxValues,
                hourEntities,
                hourResult,
                projectEntities,
                labelEntities,
                userEntities,
                teamEntities,
                selection,
              },
              timeFormat,
              query.filters,
              entityMetadata,
              currencyLookup,
              totals
            );
          }

          case widgetConst.WIDGET_CLIENTS_LIST: {
            return widgetWithDataFactory(
              widget,
              clientEntities,
              clientResult,
              // Omit maxValues here, as they're calculated on the child projects instead
              {
                projectEntities,
                projectResult,
                hourEntities,
                hourResult,
                labelEntities,
                userEntities,
                selection,
              } as RelationsType,
              timeFormat,
              query.filters,
              entityMetadata,
              currencyLookup,
              totals
            );
          }
          case widgetConst.WIDGET_CLIENTS_HBAR: {
            const maxValues = getMaxCostAndDurationSummary(clientResult, clientEntities);
            return widgetWithDataFactory(
              widget,
              clientEntities,
              clientResult,
              {
                maxValues,
                hourEntities,
                hourResult,
                projectEntities,
                labelEntities,
                userEntities,
                teamEntities,
                selection,
              },
              timeFormat,
              query.filters,
              entityMetadata,
              currencyLookup,
              totals
            );
          }

          case widgetConst.WIDGET_HOURS_CUMULATIVE_LINE:
          case widgetConst.WIDGET_HOURS_LINE: {
            const isSingleProject =
              Array.isArray(query.filters.project_ids) && query.filters.project_ids.length === 1;
            const id = query.filters.project_ids[0];
            const project =
              isSingleProject && projectEntities && typeof id === 'number'
                ? projectEntities[id]
                : null;
            return widgetWithGraphDataFactory(
              widget,
              dayEntities,
              dayResult,
              {
                query,
                cumulative: widget.widget_type === widgetConst.WIDGET_HOURS_CUMULATIVE_LINE,
                project,
              },
              entityMetadata,
              startOfWeek,
              currencyLookup
            );
          }

          case widgetConst.WIDGET_PROJECTS_DONUT:
            return widgetWithDonutDataFactory(
              widget,
              projectEntities,
              entityMetadata,
              currencyLookup
            );

          case widgetConst.WIDGET_CLIENTS_DONUT:
            return widgetWithDonutDataFactory(
              widget,
              clientEntities,
              entityMetadata,
              currencyLookup
            );

          case widgetConst.WIDGET_BILLED_VS_UNBILLED_DONUT:
            return widgetWithBilledVsUnbilledDataFactory(widget, totals, entityMetadata);

          case widgetConst.WIDGET_TEAMS_DONUT:
            return widgetWithDonutDataFactory(widget, teamEntities, entityMetadata, currencyLookup);

          case widgetConst.WIDGET_LINKED_WORK_LIST: {
            const maxValues = getMaxCostAndDurationSummary(linkedWorkResult, linkedWorkEntities);
            return widgetWithDataFactory(
              widget,
              linkedWorkEntities,
              linkedWorkResult,
              {
                maxValues,
                hourEntities,
                hourResult,
                labelEntities,
                projectEntities,
                userEntities,
                teamEntities,
                selection,
              },
              timeFormat,
              query.filters,
              entityMetadata,
              currencyLookup,
              totals
            );
          }

          case widgetConst.WIDGET_LINKED_WORK_DONUT: {
            // Ensure widget has default options with dataType
            const widgetWithOptions = {
              ...widget,
              options: {
                ...widget.options,
                dataType: widget.options?.dataType || WidgetDataType.Hours,
              },
            };
            return widgetWithDonutDataFactory(
              widgetWithOptions,
              linkedWorkEntities,
              entityMetadata,
              currencyLookup
            );
          }

          case widgetConst.WIDGET_LABELS_DONUT:
            return widgetWithDonutDataFactory(
              widget,
              labelEntities,
              entityMetadata,
              currencyLookup,
              query.filters
            );

          case widgetConst.WIDGET_BILLABLE_VS_NON_BILLABLE_DONUT:
            return widgetWithBillableVsNonBillableDataFactory(widget, totals, entityMetadata);

          default:
            debug.log('OH NOES!', widget, 'is not supported yet');
            return widget;
        }
      })
      .sort((a, b) => {
        return a.sequence - b.sequence;
      });
  }
);

export const getHoursGraphData = createSelector(
  [
    readReportDataProjectEntities,
    getReportQuery,
    readReportDataDayEntities,
    readReportDataDayResult,
    readWidgetMetadata,
    getStartOfWeek,
    getCurrenciesById,
  ],
  (projectEntities, query, dayEntities, dayResult, metadata, startOfWeek, currencyLookup) => {
    const widget = { widget_type: widgetConst.WIDGET_PLANNED_VS_LOGGED };
    const singleProject =
      Array.isArray(query.filters.project_ids) && query.filters.project_ids.length === 1;
    const id = query.filters.project_ids[0];
    const project =
      singleProject && projectEntities && typeof id === 'number' ? projectEntities[id] : null;
    return widgetWithGraphDataFactory(
      widget,
      dayEntities,
      dayResult,
      {
        query,
        cumulative: widget.widget_type === widgetConst.WIDGET_HOURS_CUMULATIVE_LINE,
        project,
      },
      metadata,
      startOfWeek,
      currencyLookup
    );
  }
);

export const getPlanningFutureExpenses = createSelector(
  [readReportDataDayEntities, readReportDataDayResult],
  (dayEntities, dayResults) => {
    const now = DateTime.now();
    const filterResults = dayResults.filter(day => DateTime.fromISO(day) > now);

    const plannedExpenses = filterResults.reduce(
      (accumulator, currentValue) => {
        const currentDay = dayEntities[currentValue];

        return {
          estimatedPlannedMinutes:
            (currentDay.estimated_duration?.total_minutes ?? 0) +
            accumulator.estimatedPlannedMinutes,
          estimatedPlannedCost:
            (currentDay.estimated_cost?.amount ?? 0) + accumulator.estimatedPlannedCost,
        };
      },
      { estimatedPlannedMinutes: 0, estimatedPlannedCost: 0 }
    );
    return { plannedExpenses };
  }
);

// Public Report filters
export const getReportFilters = readReportFilter;

// Public Report filters
export const getPublicReportFilters = readPublicReportFilter;

// Public Reports Index
export const getPublicReports = createSelector(
  [readReportEntities, readPublicReportEntities, readPublicReportResult, getPublicReportFilters],
  (reportEntities, entities, result, { userIds, report_type }) => {
    return result
      .map(key => {
        const publicReport = entities[key];
        const sourceReport = reportEntities?.[publicReport.report_id ?? -1];
        return publicReportListItemFactory(publicReport, sourceReport);
      })
      .reduce<Array<PublicReportListItem>>((prev, report) => {
        if (report == null) {
          return prev;
        }
        const includeReport = (report: PublicReportListItem) => {
          prev.push(report);
          return prev;
        };

        if (!!userIds.length && userIds.includes(report?.id)) includeReport(report);
        if (report.live && report_type === 'live') includeReport(report);
        if (!report.live && report_type === 'snapshot') includeReport(report);

        return prev;
      }, []);
  }
);

// Reports Index
export const getReports = createSelector(
  [readReportEntities, readReportResult, getPublicReports, getPinnedReportIdsSet, getReportFilters],
  (entities, result, publicReports, pinnedReportIdsSet, filter) => {
    const sortBy = filter.sort_by as keyof PickByValue<ReportEntity, SortableValue>;

    return result
      .map(key => {
        return entities[key]
          ? reportListItemFactory(entities[key], publicReports, pinnedReportIdsSet)
          : null;
      })
      .filter(report =>
        filter.user_ids.length ? filter.user_ids.includes(report?.createdBy.id ?? 0) : true
      )
      .filter(notNull)
      .sort((a, b) => {
        const aRecord = entities[a.id];
        const bRecord = entities[b.id];

        if (filter.order === 'asc') {
          return naturalCompare(aRecord[sortBy], bRecord[sortBy]);
        }
        return naturalCompare(bRecord[sortBy], aRecord[sortBy]);
      });
  }
);

export const getPinnedReports = createSelector(
  [getPinnedReportEntities, getPinnedReportIds, getReportFilters],
  (entities, result, filter) => {
    return result
      .map(key => (entities[key] ? reportListItemFactory(entities[key]) : null))
      .filter(report =>
        filter.user_ids.length ? filter.user_ids.includes(report?.createdBy.id ?? 0) : true
      )
      .filter(notNull)
      .sort((a, b) => {
        const aRecord = entities[a.id];
        const bRecord = entities[b.id];

        return naturalCompare(aRecord.name, bRecord.name);
      });
  }
);
export const getUnpinnedReports = createSelector(
  [readReportEntities, readReportResult, getPinnedReportIds, getReportFilters, getPublicReports],
  (entities, result, pinnedResult, filter, publicReports) => {
    const sortBy = filter.sort_by as keyof PickByValue<ReportEntity, SortableValue>;
    const reportIds = result.filter(r => !pinnedResult.includes(r));

    return reportIds
      .map(key => {
        return entities[key] ? reportListItemFactory(entities[key], publicReports) : null;
      })
      .filter(report =>
        filter.user_ids.length ? filter.user_ids.includes(report?.createdBy.id ?? 0) : true
      )
      .filter(notNull)
      .sort((a, b) => {
        const aRecord = entities[a.id];
        const bRecord = entities[b.id];

        if (filter.order === 'asc') {
          return naturalCompare(aRecord[sortBy], bRecord[sortBy]);
        }
        return naturalCompare(bRecord[sortBy], aRecord[sortBy]);
      });
  }
);

export const getReportListFilter = createSelector(
  [readReportListFilter, getUser],
  (filter, user) => {
    return (user.properties[PREFERRED_REPORT_FILTER] as ReportFilterTypes) ?? filter;
  }
);

export const getReportDraft = readReportDraft;

const getReportQueryDataEntities = createSelector(
  [
    getAllUsers,
    getClients,
    getAllLoadedProjects,
    getTags,
    getHours,
    getTagsWithChildren,
    getTeamsAsDropdownOptions,
    getNonDeletedHourStates,
  ],
  (users, clients, projects, tags, hours, tagEntities, teams, hourStates) => ({
    users,
    clients,
    projects,
    tags,
    hours,
    tagEntities,
    teams,
    hourStates,
  })
);

export const getReportQueryData = createSelector(
  [
    getCurrentAccount,
    getUser,
    getReportQuery,
    getUserPermissions,
    getProjectVariantMap,
    getReportQueryDataEntities,
    getMinimumWidgetWidth,
  ],
  (
    account,
    user,
    query,
    permissions,
    projectMap,
    { users, clients, projects, tags, hours, tagEntities, teams, hourStates },
    minimumWidgetWidth
  ) => {
    const filterCounts = {
      projects: query.filters.project_ids.length,
      totalProjects: account.total_projects_count,
      activeProjects: account.active_projects_count,
      users: query.filters.user_ids.length,
      labels: query.filters.label_ids.length,
      teams: query.filters.team_ids.length,
      hourStates: query.filters.state_ids.length,
      totalTags: Object.keys(tagEntities).length,
      totalTeams: teams.length,
    };

    const { hasModifiedFilters, buttonText } = determineFilterStatus(query, filterCounts);

    const appliedLabelIds = hours.reduce((prev: Array<number>, curr) => {
      return [...prev, ...curr.label_ids];
    }, []);

    const filteredProjects = filterProjectsByStatusOrIds(query.filters.project_ids, projectMap);

    const labelIds = filteredProjects.map(p => p.label_ids);

    const showAllTags = filteredProjects.some(p => p.enable_labels === TagSettings.All);

    return {
      account,
      isUserAdmin: !!user.admin,
      query,
      users,
      projects,
      clients,
      tags:
        labelIds.length === 0 || showAllTags
          ? tags
          : getCommonTagsForFiltering(tagEntities, labelIds, appliedLabelIds),
      teams,
      hourStates,
      hasModifiedFilters,
      buttonText,
      permissions,
      ui: {
        showFilterHint:
          !user.properties[HAS_FILTERED_REPORTS] && !user.properties[HAS_ADDED_WIDGET_TO_REPORT],
        minimumWidgetWidth,
        isHighLevelReportsFeatureEnabled: !!account?.features?.high_level_reports,
      },
    };
  }
);

export const getUnsavedDraftStatus = createSelector(
  [readReportDraftSignature, readReportDraft, readReportQuery],
  (draftSignature, draft, query) => {
    return !draft.id || draftSignature !== getSignatureFromDraftAndQuery(draft, query);
  }
);

export const getIsShowingBatchActionsStatus = createSelector([readReportSelection], selection => {
  return selection.hourIds.length > 0;
});

// SuperReportsDetailsRoute selectors are combined here.
export const getReportDetailsHeaderData = createSelector(
  [
    getCurrentAccount,
    getReportDraft,
    getUnsavedDraftStatus,
    getUserPermissions,
    getXhrStatusUiState,
    getPublicReports,
    getReportQuery,
    getMinimumWidgetWidth,
    getUser,
    getCurrentAccountColumnOrders,
  ],
  (
    account,
    draft,
    hasUnsavedChanges,
    permissions,
    xhrStatus,
    publicReports,
    { filters },
    minimumWidgetWidth,
    user,
    columns
  ) => {
    const hasDraft = !!(Object.keys(draft).length > 0);
    const hasValidReportDraft = !!(hasDraft && draft.name?.length);
    const isLoading = xhrStatus.isLoading || (!xhrStatus.hasErrors && !draft.hasLoadedInitialData);
    const isSaving = xhrStatus.isSaving;
    const isSaveDisabled = isLoading || isSaving || !hasValidReportDraft;
    const isShareDisabled = isSaveDisabled;
    // @todo This needs to take query.filters and widget columns into account
    // Really want to avoid setting draft._unsaved or similar if we can avoid it.
    // const hasUnsavedChanges = hasDraft && (draft._unsaved || !draft.id);

    const shouldShowActions = permissions.canCreate.reports || permissions.canDelete.reports;

    const publicReportIds =
      draft.id != null ? publicReports.filter(r => r.source?.id === draft.id).map(r => r.id) : [];

    const anyFilters: Array<string> = [];
    if (!filters.user_ids.length) {
      anyFilters.push('user');
    }
    if (!filters.project_ids.length) {
      anyFilters.push('project');
    }

    return {
      account,
      anyFilters,
      draft,
      isLoading,
      isSaving,
      isSaveDisabled,
      isShareDisabled,
      hasUnsavedChanges,
      permissions,
      publicReportIds,
      shouldShowActions,
      minimumWidgetWidth,
      columns,
      showReorderHint: !user.properties[HAS_SEEN_CAN_REORDER],
    };
  }
);

export const getRequiredEntitiesForReport = createSelector(
  [getWidgets, readWidgetMetadata],
  (widgets, widgetMetadata): Array<EntityDataKey> =>
    Array.from(
      new Set(
        widgets
          .map(widget => widgetMetadata[widget.widget_type]?.entity)
          .concat(EntityDataKey.Totals)
          .filter(notNull)
      )
    )
);

const getReportDetailsUi = createSelector(
  [
    getXhrStatusUiState,
    getUser,
    getIsShowingBatchActionsStatus,
    readEmailedReport,
    getAssetRevisionStatus,
    getIsAppInitialized,
    getAccountFeatures,
    getMinimumWidgetWidth,
    getHasMultipleCurrencies,
  ],
  (
    xhrStatus,
    user,
    isShowingBatchActions,
    emailedReport,
    assetRevisionStatus,
    isAppInitialized,
    features,
    minimumWidgetWidth,
    hasMultipleCurrencies
  ) => {
    return {
      isSavingOrExporting: xhrStatus.isSaving,
      showMoney: !user.hide_hourly_rate,
      isShowingBatchActions,
      showAddWidgetHint:
        (user.properties[HAS_FILTERED_REPORTS] && !user.properties[HAS_ADDED_WIDGET_TO_REPORT]) ||
        false,
      emailedReport,
      customAssetRevision: assetRevisionStatus.isCustomRevisionActive
        ? assetRevisionStatus.revision!
        : undefined,
      isAppInitialized,
      isBudgetFeatureEnabled: !!features.project_budget,
      minimumWidgetWidth,
      hasMultipleCurrencies,
      isRecurringBudgetsEnabled: !!features.recurring_budget,
    };
  }
);

const getIsLoadingEntities = createSelector([readXhrStatus], xhrStatus => {
  return Object.values(EntityDataKey).reduce(
    (temp, key) => {
      temp[key] = xhrStatus.fetching.some(s => s.key === key);
      return temp;
    },
    {} as Record<EntityDataKey, boolean>
  );
});

// SuperReportsDetailsRoute selectors are combined here.
export const getReportDetailsPage = createSelector(
  [
    getReportQuery,
    getReportDraft,
    getWidgets,
    getRequiredEntitiesForReport,
    readReportTotals,
    getReportDetailsUi,
    getXhrStatusUiState,
    getWidgetMetadata,
    getIsLoadingEntities,
    getWidgetMetadataXhrStatus,
  ],
  (
    query,
    draft,
    widgets,
    entities,
    totals,
    ui,
    xhrStatus,
    widgetMetadata,
    isLoadingEntities,
    widgetMetadataXhrStatus
  ) => {
    const hasDraft = Object.keys(draft).length > 1; // draft.widgets will always exist

    return {
      query,
      totals,
      draft,
      hasExistingReportDraft: hasDraft,
      widgets,
      entities,
      ui,
      ...xhrStatus,
      isLoadingEntities,
      widgetMetadata,
      isLoadingWidgetMetadata: widgetMetadataXhrStatus.isLoading,
    };
  }
);

export const getReportIndexShowStatus = createSelector(
  [
    getUser,
    getUnpinnedReports,
    getPinnedReports,
    getReports,
    readReportViewStatus,
    getReportListFilter,
    readXhrStatus,
    readReportMaybeHasMore,
  ],
  (
    user,
    unpinnedReports,
    pinnedReports,
    reports,
    reportViewStatus,
    reportListFilter,
    xhrStatus,
    maybeHasMore
  ) => {
    const showAllReports =
      user.properties[PREFERRED_REPORT_FILTER] === 'pinned-and-recent'
        ? false
        : reportListFilter === 'all';

    const hasReports = !!unpinnedReports.length || !!pinnedReports.length;
    const reportFetching = !!xhrStatus.fetching.find(k => k.key === 'reports');
    const preferredReportView = user.properties[PREFERRED_REPORT_VIEW]
      ? user.properties[PREFERRED_REPORT_VIEW]
      : reportViewStatus;

    const noRecentReportsIncluded = pinnedReports.length >= MINIMUM_REPORTS_ON_DASHBOARD;
    const pinnedAndRecent = noRecentReportsIncluded
      ? pinnedReports
      : [
          ...pinnedReports,
          ...unpinnedReports.slice(0, MINIMUM_REPORTS_ON_DASHBOARD - pinnedReports.length),
        ];
    return {
      emptyScreen: showAllReports ? !hasReports && !reportFetching : !pinnedAndRecent.length,
      reports: hasReports && !reportFetching,
      reportPlaceholders: reportFetching && !hasReports,
      maybeHasMoreReports: maybeHasMore && reportListFilter !== 'pinned-and-recent',
      reportsToShow: showAllReports ? reports : pinnedAndRecent,
      reportViewGrid: preferredReportView === 'grid',
      reportViewList: preferredReportView === 'list',
      noPinnedReportsIncluded: !pinnedReports.length,
    };
  }
);

// SuperReportsIndexRoute selectors are combined here.
export const getReportIndexPage = createSelector(
  [
    getReports,
    getPinnedReports,
    getPublicReports,
    getReportFilters,
    getReportIndexShowStatus,
    getPublicReportFilters,
    getReportListFilter,
    getUserPermissions,
    getAllUsers,
    getXhrStatus,
    readPublicReportMaybeHasMore,
  ],
  (
    reports,
    pinnedReports,
    publicReports,
    reportFilter,
    show,
    publicFilter,
    reportListFilter,
    permissions,
    users,
    xhrStatus,
    publicReportMaybeHasMore
  ) => {
    const publicReportFetching = !!xhrStatus.fetching.find(k => k.key === 'public-reports');
    const reportTemplatesFetching = !!xhrStatus.fetching.find(k => k.key === 'reports');

    return {
      show,
      reports,
      pinnedReports,
      reportFilter,
      publicReports,
      publicFilter,
      publicReportFetching,
      publicReportMaybeHasMore,
      reportTemplatesFetching,
      reportListFilter,
      permissions,
      users,
    };
  }
);

// SuperReportsPublicRoute selectors are combined here.
export const getPublicReportDetailsPage = createSelector(
  [
    getCurrentAccount,
    getReportQuery,
    getReportDraft,
    getWidgets,
    getXhrStatusUiState,
    readPublicReportTimestamp,
    getUserTimeFormat,
    readReportTotals,
    getWidgetMetadata,
    getMinimumWidgetWidth,
  ],
  (
    account,
    query,
    draft,
    widgets,
    xhrStatusUiState,
    timestamp,
    userTimeFormat,
    totals,
    widgetMetadata,
    minimumWidgetWidth
  ) => {
    return {
      account,
      draft,
      query,
      timestamp,
      totals,
      userTimeFormat,
      widgets,
      widgetMetadata,
      minimumWidgetWidth,
      ...xhrStatusUiState,
    };
  }
);

export const getFilteredQuery = createSelector(
  [getReportQuery],
  (originalQuery): ReportQueryExtended => {
    const query = { ...originalQuery };

    if (originalQuery.span === 'unrestricted') {
      query.from = UNRESTRICTED_FROM;
      query.to = UNRESTRICTED_TO;
    }

    return query;
  }
);

export const getFilteredQueryInApiFormat = createSelector(
  [
    getFilteredQuery,
    getTagsWithChildren,
    getHasHourStatesFeature,
    getHourStates,
    getHasLoadedHourStates,
    getHasTicIntegrationsSupport,
  ],
  (
    query,
    tagsWithChildren: TagsWithChildrenDictionary,
    hasHourStatesFeature,
    hourStates,
    hasLoadedHourStates,
    hasTicIntegrationsSupport
  ) => {
    // TagFilterDropdown stores explicit leaf IDs (it expands parent checkboxes to their
    // visible children). In non-AND mode we expand in two directions so all relevant
    // entries are found regardless of how/when they were tagged:
    //   - Parent tag in label_ids (e.g. from a saved filter): include itself + children
    //     so entries tagged with any child are also returned (TT-4890).
    //   - Child tag in label_ids (dropdown converted parent → children): include its
    //     parent so entries tagged directly with the parent before children existed are
    //     also returned (edge case: parent-only entry pre-dates child tag creation).
    // In AND mode ("Match all tags") skip expansion entirely — inflating label_ids.size
    // breaks the backend's COUNT(DISTINCT) = label_ids.size check (TT-4894).
    const isAndFilter = query.filters.label_filter === TagFilterMode.AND;
    const label_ids = isAndFilter
      ? query.filters.label_ids
      : [
          ...new Set(
            query.filters.label_ids.flatMap(tagId => {
              const tag = tagsWithChildren[tagId];
              if (tag?.children?.length) {
                // Parent tag: include itself and all its children
                return [tagId, ...tag.children.map(t => t.id)];
              }
              if (tag?.parent_id) {
                // Child tag: include itself and its parent
                return [tagId, tag.parent_id];
              }
              return tagId;
            })
          ),
        ];

    let filters = omit(query.filters, ['billed', 'locked']);
    if (!hasHourStatesFeature && hasLoadedHourStates) {
      filters = transformLegacyFiltersToStateIds(query.filters, hourStates);
    }

    return {
      ...query,
      filters: {
        ...filters,
        label_ids,
      },
      client_ids: query.client_ids,
      include_linked_metadata: hasTicIntegrationsSupport,
    };
  }
);

const getTimesheetScope = (_: unknown, props: { scope?: TimesheetScopes }) =>
  props.scope ?? TimesheetScopes.All;

export const getHoursAsDatatableRows = createSelector(
  [
    getHours,
    getReportQueryData,
    getTagEntities,
    getPinnedProjectIdsSet,
    getCurrenciesById,
    getHasHourStatesFeature,
    getUserRoleId,
    getHourStates,
    getUserLedUserIds,
    getDayProperties,
    getIsUserAdmin,
  ],
  (
    hours,
    reportQueryData,
    allTags,
    pinnedProjectIdsSet,
    currencyLookup,
    hasHourStatesFeature,
    userRoleId,
    hourStates,
    userLedUserIds,
    dayProperties,
    isUserAdmin
  ) => {
    const sortField = mapTableColumnToSort(reportQueryData.query.sort ?? COLUMN_DAY);
    const sortDirection = reportQueryData.query.order ?? 'desc';

    return enrichProjectsOnHours(
      filterHoursByState(hours, reportQueryData.query.filters),
      pinnedProjectIdsSet
    )
      .sort((a, b) => sortReportableValues(a, b, sortField, sortDirection))
      .map<TimesheetsDatatableRow>(hour => {
        const parentTags = reportQueryData.tags.filter(
          tag =>
            hour.label_ids.includes(tag.id) ||
            tag.children?.some(child => hour.label_ids.includes(child.id))
        );
        const tags = parentTags.map<TagBase>(tag => {
          const existingChildTag = tag.children?.find(tag => hour.label_ids.includes(tag.id));
          const missingChildTagId = !existingChildTag
            ? allTags[tag.id].children?.find(childTag => hour.label_ids.includes(childTag))
            : undefined;
          const childTag =
            existingChildTag ?? (missingChildTagId ? allTags[missingChildTagId] : undefined);

          return {
            emoji: childTag?.emoji ?? tag.emoji,
            name: childTag?.name ?? tag.name,
            id: childTag?.id ?? tag.id,
            parent: {
              id: tag.id,
              name: tag.name,
              emoji: tag.emoji,
            },
          };
        });

        const costAndDuration = hourToGenericWidgetValues(hour);
        const currency = currencyLookup[hour.cost.currency_code];
        const userIsTeamLead = !!userLedUserIds?.has(hour.user.id);
        const state = hourStates[hour.state?.id || defaultStateId] ?? hour.state ?? defaultState;
        const isProjectArchived = !hour.project.base.active;

        // Map all external links with metadata
        const linkedWorkItems =
          hour.external_links && hour.external_links.length > 0
            ? hour.external_links.map(link => ({
                provider_type: link.provider_type,
                uri: link.uri,
                html_url: link.html_url,
                external_code: link.external_code,
                title: link.title,
                status: link.status,
                jira_custom_field: link.jira_custom_field,
              }))
            : undefined;

        const row: TimesheetsDatatableRow = {
          id: hour.id,
          resource: 'hour',
          project: hour.project.base,
          user: { name: hour.user.name, avatar: hour.user.avatar },
          tags,
          canManage: hour.manage,
          // Admins can move entries to/from archived projects (TT-4409), so
          // they may select archived-project rows. Non-admins keep the lock.
          canSelect: isUserAdmin || !isProjectArchived,
          selectionTooltip:
            isProjectArchived && !isUserAdmin
              ? 'Restore archived project to edit entry'
              : undefined,
          date: DateTime.fromISO(hour.day).toLocaleString(DateTime.DATE_FULL),
          note: hour.note,
          // Theoretically these should work via hour states as well
          /* isBilled: hour.billed,
          isLocked: hour.locked && hour.locked_reason === HourLockedReasons.Locked,
          isInvoiced: hour.locked && hour.locked_reason === HourLockedReasons.Invoiced, */
          isDayLocked: isDayLockedForUser(hour.user.id, dayProperties[hour.day]),
          state,
          isBilled: state.billed,
          isLocked: isHourLocked(state, {
            states: hourStates,
            userRoleId,
            userIsTeamLead,
          }),
          isInvoiced: state.name === 'Invoiced', // TODO: Update when "old" system is gone
          markedAsUnbillable:
            (!hour.billable && !!hour.cost.amount) ||
            (!hour.billable && !!hour.estimated_cost?.amount),
          billable: hour.billable ? 'Billable' : '-',
          ...costAndDuration,
          loggedMoney: { value: costAndDuration.loggedMoney, currency },
          plannedMoney: { value: costAndDuration.plannedMoney, currency },
          billedMoney: costAndDuration.billedMoney
            ? formatCurrency(currency, costAndDuration.billedMoney, 2)
            : undefined,
          unbilledMoney: costAndDuration.unbilledMoney
            ? formatCurrency(currency, costAndDuration.unbilledMoney, 2)
            : undefined,
          linkedWorkItems,
        };

        return row;
      });
  }
);

export const getHoursAsWidgetRows = createSelector(
  [
    getHours,
    getReportQueryData,
    getTagEntities,
    readReportSelection,
    getPinnedProjectIdsSet,
    getTimesheetScope,
    getUserTimeFormat,
    getCurrenciesById,
    getHasHourStatesFeature,
    getUserRoleId,
    getHourStates,
    getUserLedUserIds,
    getDayProperties,
  ],
  (
    hours,
    reportQueryData,
    allTags,
    selection,
    pinnedProjectIdsSet,
    scope,
    timeFormat,
    currencyLookup,
    hasHourStatesFeature,
    userRoleId,
    hourStates,
    userLedUserIds,
    dayProperties
  ) => {
    const sortField = mapTableColumnToSort(reportQueryData.query.sort ?? COLUMN_DAY);
    const sortDirection = reportQueryData.query.order ?? 'desc';
    return enrichProjectsOnHours(
      filterHoursByState(hours, reportQueryData.query.filters),
      pinnedProjectIdsSet
    )
      .sort((a, b) => sortReportableValues(a, b, sortField, sortDirection))
      .map<GenericWidgetRow>(hour => {
        const parentTags = reportQueryData.tags.filter(
          tag =>
            hour.label_ids.includes(tag.id) ||
            tag.children?.some(child => hour.label_ids.includes(child.id))
        );
        const tags = parentTags.map<TagBase>(tag => {
          const existingChildTag = tag.children?.find(tag => hour.label_ids.includes(tag.id));
          const missingChildTagId = !existingChildTag
            ? allTags[tag.id].children?.find(childTag => hour.label_ids.includes(childTag))
            : undefined;
          const childTag =
            existingChildTag ?? (missingChildTagId ? allTags[missingChildTagId] : undefined);

          return {
            emoji: childTag?.emoji ?? tag.emoji,
            name: childTag?.name ?? tag.name,
            id: childTag?.id ?? tag.id,
            parent: {
              id: tag.id,
              name: tag.name,
              emoji: tag.emoji,
            },
          };
        });

        const row: GenericWidgetRow = {
          ...widgetRowHourFactory(hour, timeFormat, selection.hourIds, currencyLookup),
          id: hour.id,
          labels: tags,
          project: hour.project.base,
          isDayLocked: isDayLockedForUser(hour.user.id, dayProperties[hour.day]),
        };

        if (!(scope & TimesheetScopes.Project)) {
          row.color = getProjectColor(hour.project.base);
          row.name = hour.project.base.name;
        } else {
          row.avatar = hour.user.avatar.medium_retina;
          row.name = hour.user.name;
        }

        if (hasHourStatesFeature) {
          const userIsTeamLead = !!userLedUserIds?.has(hour.user.id);
          row.state = hourStates[hour.state?.id || defaultStateId] ?? hour.state ?? defaultState;
          row.isBilled = row.state.billed;
          row.isLocked = isHourLocked(row.state, {
            states: hourStates,
            userRoleId,
            userIsTeamLead,
          });
          row.isInvoiced = row.state.name === 'Invoiced'; // TODO: Update when "old" system is gone
        }

        return row;
      });
  }
);

export const getSelectionTotals = createSelector(
  [getHours, readReportSelection, getAccountCurrency],
  (hours, selection, accountCurrency): GenericWidgetRow | null => {
    if (selection.hourIds?.length === 0) {
      return null;
    }

    const selectedCount = selection.hourIds.length;
    const totalCount = hours.length;
    const selectionLabel =
      selectedCount === totalCount
        ? `All items selected`
        : `${selectedCount} of ${totalCount} selected`;

    const selectedHours = hours.filter(hour => selection.hourIds.includes(hour.id));
    const singleCurrency = extractSingleCurrency(
      selectedHours,
      hour => (hour.project as Project)?.currency
    );

    const selectedValues = selectedHours.map(hourToGenericWidgetValues);
    const summedValues = reduceBySummingAttributes(selectedValues);

    return {
      name: selectionLabel,
      ...formatGenericWidgetValues(summedValues, singleCurrency ?? accountCurrency),
      hasMultipleCurrencies: !singleCurrency,
    };
  }
);

/**
 * Ordered list of widget columns used to sort the timesheet columns.
 */
const orderedTimesheetColumns = [
  COLUMN_DAY,
  COLUMN_PROJECT,
  COLUMN_STATE,
  COLUMN_LOGGED_HOURS,
  COLUMN_LOGGED_MONEY,
  COLUMN_ESTIMATED_HOURS,
  COLUMN_ESTIMATED_MONEY,
  COLUMN_BILLABLE_HOURS,
  COLUMN_NON_BILLABLE_HOURS,
  COLUMN_BILLED,
  ...ITEMS_WITH_NO_TABLE_COLUMN,
] as const;

const getAccountData = createSelector(
  [getAccountFeatures, getCurrentAccountColumnOrders, getAccountReportSettings],
  (features, columnOrders, reportSettings) => ({ features, ...columnOrders, reportSettings })
);

const getTimesheetColumns = createSelector(
  [readTimesheetColumns, readTimesheetTaskColumns],
  (columns, taskColumns) => ({ columns, taskColumns })
);

export const getTimesheet = createSelector(
  [
    getReportQueryData,
    getHoursAsWidgetRows,
    getSelectionTotals,
    readTimesheetMaybeHasMore,
    readReportSelection,
    getTimesheetColumns,
    getUserPermissions,
    getXhrStatus,
    getAccountData,
    getWidgetMetadata,
    getHourXhrUIState,
    getUser,
    getIsAppInitialized,
    getHoursAsDatatableRows,
    readTimesheetTotalCount,
  ],
  (
    reportQueryData,
    hours,
    selectionTotals,
    maybeHasMore,
    selection,
    { columns, taskColumns },
    userPermissions,
    xhrStatus,
    { features, tasksColumnOrder, hoursColumnOrder, reportSettings },
    metadata,
    hourXhrUiState,
    user,
    isAppInitialized,
    datatableRows,
    totalCount
  ) => {
    const queryAsFilter: HoursDetailsFilter = {
      ...HOUR_DETAILS_INITIAL_FILTER,
      billedState: reportQueryData.query.filters.billed,
      from: reportQueryData.query.from ?? '',
      labelIds: reportQueryData.query.filters.label_ids,
      projectIds: reportQueryData.query.filters.project_ids,
      projectIdsNotIn: reportQueryData.query.filters.project_ids_not_in,
      teamIds: reportQueryData.query.filters.team_ids,
      timespan: reportQueryData.query.span ?? 'unrestricted',
      to: reportQueryData.query.to ?? '',
      userIds: reportQueryData.query.filters.user_ids,
      page: reportQueryData.query.page ?? HOUR_DETAILS_INITIAL_FILTER.page,
      limit: reportQueryData.query.limit ?? HOUR_DETAILS_INITIAL_FILTER.limit,
      sortBy: {
        field: reportQueryData.query.sort ?? HOUR_DETAILS_INITIAL_FILTER.sortBy.field,
        direction: reportQueryData.query.order ?? HOUR_DETAILS_INITIAL_FILTER.sortBy.direction,
      },
      hourStateIds: reportQueryData.query.filters.state_ids,
      columns,
      taskColumns,
      resource: '',
      external_link_filter: reportQueryData.query.filters.external_link_filter,
      sync_status_filter: reportQueryData.query.filters.sync_status_filter,
    };

    const batchSelection: HoursDetailsBatchSelection = {
      eventIds: selection.hourIds,
      projectIds: [],
      labelIds: [],
      billedState: reportQueryData.query.filters.billed,
    };

    const widget: WidgetWithData = newReportWidgetFactory({
      widget_type: 'TIMESHEET',
    });

    widget.rows = hours;
    widget.columns = {
      names: intersection(
        orderedTimesheetColumns,
        [COLUMN_DAY].concat(
          columns.filter(c => c !== COLUMN_STATE || features.hour_states).map(mapHourColumnToTable)
        )
      ),
      sort_by: queryAsFilter.sortBy.field,
      order: queryAsFilter.sortBy.direction!,
    };
    widget.title = '';
    if (selectionTotals) {
      widget.footer = selectionTotals;
    }

    return {
      ...reportQueryData,
      batchSelection,
      widget,
      hours,
      queryAsFilter,
      metadata,
      user,
      tasksColumnOrder,
      hoursColumnOrder,
      reportSettings,
      ui: {
        ...reportQueryData.ui,
        isExporting: xhrStatusUIState(xhrStatus, 'timesheet').isSaving,
        isSaving: xhrStatusUIState(xhrStatus).isSaving,
        isLoading:
          xhrStatusUIState(xhrStatus, 'timesheet').isLoading ||
          hourXhrUiState.isLoading ||
          !isAppInitialized,
        canUpdateLocked: userPermissions.canUpdate.projects,
        maybeHasMore,
        isBudgetFeatureEnabled: !!features.project_budget,
        hasCapacityReports: !!features.capacity_reports,
      },
      selectionMap: selection.hourIds.reduce((map, id) => {
        map[id] = true;
        return map;
      }, {} as RowSelectionState),
      datatableRows,
      totalCount,
    };
  }
);

export const getClientReportQueryData = createSelector(
  [readClientReportQuery, getCurrentAccount, getReportQueryDataEntities, getMinimumWidgetWidth],
  (query, account, { tagEntities, teams }, minimumWidgetWidth) => {
    const filterCounts = {
      projects: query.filters.project_ids.length,
      totalProjects: account.total_projects_count,
      activeProjects: account.active_projects_count,
      users: query.filters.user_ids.length,
      labels: query.filters.label_ids.length,
      teams: query.filters.team_ids.length,
      hourStates: query.filters.state_ids.length,
      totalTags: Object.keys(tagEntities).length,
      totalTeams: teams.length,
    };
    const { hasModifiedFilters } = determineFilterStatus(query, filterCounts);

    return {
      account,
      query,
      hasModifiedFilters,
      isMissingQueryRequirements: !query.from || !query.to,
      minimumWidgetWidth,
    };
  }
);

export const getClientHourSummaryData = createSelector(
  [
    getUser,
    getAccountData,
    getHourXhrUIState,
    getXhrStatus,
    getClientReportQueryData,
    readHourSummaryData,
    getIsAppInitialized,
  ],
  (
    user,
    accountData,
    hourXhrUiState,
    xhrStatus,
    clientReportQueryData,
    hourSummaryData,
    isAppInitialized
  ) => {
    const queryAsFilter: HoursDetailsFilter = {
      ...HOUR_DETAILS_INITIAL_FILTER,
      billedState: clientReportQueryData.query.filters.billed,
      from: clientReportQueryData.query.from ?? '',
      labelIds: clientReportQueryData.query.filters.label_ids,
      projectIds: clientReportQueryData.query.filters.project_ids,
      projectIdsNotIn: clientReportQueryData.query.filters.project_ids_not_in,
      teamIds: clientReportQueryData.query.filters.team_ids,
      timespan: clientReportQueryData.query.span ?? 'unrestricted',
      to: clientReportQueryData.query.to ?? '',
      userIds: clientReportQueryData.query.filters.user_ids,
      page: clientReportQueryData.query.page ?? HOUR_DETAILS_INITIAL_FILTER.page,
      limit: clientReportQueryData.query.limit ?? HOUR_DETAILS_INITIAL_FILTER.limit,
      sortBy: {
        field: clientReportQueryData.query.sort ?? HOUR_DETAILS_INITIAL_FILTER.sortBy.field,
        direction:
          clientReportQueryData.query.order ?? HOUR_DETAILS_INITIAL_FILTER.sortBy.direction,
      },
      hourStateIds: clientReportQueryData.query.filters.state_ids,
      resource: 'client',
      external_link_filter: clientReportQueryData.query.filters.external_link_filter,
      sync_status_filter: clientReportQueryData.query.filters.sync_status_filter,
    };

    const totalCount = hourSummaryData.totalCount;
    const maybeHasMore = hourSummaryData.ui.maybeHasMore;

    return {
      ...clientReportQueryData,
      user,
      accountData,
      queryAsFilter,
      hourSummaries: hourSummaryData.hourSummaries,
      ui: {
        isLoading:
          xhrStatusUIState(xhrStatus, 'client').isLoading ||
          hourXhrUiState.isLoading ||
          !isAppInitialized,
        maybeHasMore,
      },
      totalCount,
      columns: {
        names: hourSummaryData.columns,
        sort_by: clientReportQueryData.query.sort ?? 'client',
        order: clientReportQueryData.query.order ?? 'desc',
      },
    };
  }
);

export const getReportExportSettings = createSelector(
  [readReportExportSettings, getUser],
  (settings, user): ReportExportSettingsPayload => {
    return { ...settings, recipients: settings.recipients ?? `${user.email}` };
  }
);

export const getNextExportDate = readNextExportDate;
